/**
 * `auditDataClient` — Injectable read client for audit surfaces.
 *
 * Implements GVW-130..133 per BACKEND_NOTES.md BE-024. The fetch function
 * is injectable so tests can stub network calls. Backend-not-deployed
 * state is typed and returned on 404/501; we do not fake success.
 *
 * Cite:
 *   - design/30-engineering/ui-implementation/GVW-{130,131,132,133}/brief.md
 *   - BACKEND_NOTES.md BE-024 (session audit reader)
 *   - WP-E8 dispatch requirements
 */

export interface AuditRow {
  id: string;
  sessionId: string;
  messageIdx: number;
  createdAt: string; // ISO8601
  actor: string;
  target: string;
  action: string;
  scope: string;
  outcome: 'success' | 'blocked' | 'error' | 'pending';
  costClass: 'free' | 'cheap' | 'expensive';
  confidence?: number;
  resourceId?: string;
  errorCode?: string;
  reasonCode?: string;
  retentionUntil: string; // ISO8601
}

export interface AuditListResponse {
  rows: AuditRow[];
  cursor?: string;
  hasMore: boolean;
  totalCount: number;
}

export interface AuditFilters {
  actor?: string;
  target?: string;
  action?: string;
  timeRange?: {
    start: string; // ISO8601
    end: string;   // ISO8601
  };
  scope?: string;
  outcome?: AuditRow['outcome'];
  costClass?: AuditRow['costClass'];
  confidenceRange?: {
    min: number;
    max: number;
  };
}

export type AuditClientState = 
  | { kind: 'loading' }
  | { kind: 'success'; data: AuditListResponse }
  | { kind: 'error'; message: string; code?: string }
  | { kind: 'backend-not-deployed' };

/**
 * Injectable fetch implementation — tests can provide a stub.
 * Production code should pass `globalThis.fetch`.
 */
export type FetchFn = typeof fetch;

export class AuditDataClient {
  constructor(private fetchFn: FetchFn = globalThis.fetch) {}

  async listAuditRows(
    sessionId?: string,
    filters?: AuditFilters,
    cursor?: string,
  ): Promise<AuditClientState> {
    try {
      const url = new URL('/api/v1/audit/session', globalThis.location.origin);
      
      if (sessionId) {
        url.searchParams.set('session_id', sessionId);
      }
      if (filters?.actor) {
        url.searchParams.set('actor', filters.actor);
      }
      if (filters?.target) {
        url.searchParams.set('target', filters.target);
      }
      if (filters?.action) {
        url.searchParams.set('action', filters.action);
      }
      if (filters?.timeRange) {
        url.searchParams.set('time_start', filters.timeRange.start);
        url.searchParams.set('time_end', filters.timeRange.end);
      }
      if (filters?.scope) {
        url.searchParams.set('scope', filters.scope);
      }
      if (filters?.outcome) {
        url.searchParams.set('outcome', filters.outcome);
      }
      if (filters?.costClass) {
        url.searchParams.set('cost_class', filters.costClass);
      }
      if (filters?.confidenceRange) {
        url.searchParams.set('confidence_min', filters.confidenceRange.min.toString());
        url.searchParams.set('confidence_max', filters.confidenceRange.max.toString());
      }
      if (cursor) {
        url.searchParams.set('cursor', cursor);
      }

      const response = await this.fetchFn(url.toString(), {
        method: 'GET',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
      });

      if (response.status === 404 || response.status === 501) {
        return { kind: 'backend-not-deployed' };
      }

      if (!response.ok) {
        await response.text().catch(() => 'Unknown error'); // Error body for debugging
        return {
          kind: 'error',
          message: `Failed to load audit rows: ${response.status} ${response.statusText}`,
          code: `HTTP_${response.status}`,
        };
      }

      const data: AuditListResponse = await response.json();
      return { kind: 'success', data };
    } catch (error) {
      return {
        kind: 'error',
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async getAuditRow(id: string): Promise<AuditClientState> {
    try {
      const url = new URL('/api/v1/audit/session', globalThis.location.origin);
      url.searchParams.set('row_id', id);

      const response = await this.fetchFn(url.toString(), {
        method: 'GET',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
      });

      if (response.status === 404 || response.status === 501) {
        return { kind: 'backend-not-deployed' };
      }

      if (!response.ok) {
        await response.text().catch(() => 'Unknown error'); // Error body for debugging
        return {
          kind: 'error',
          message: `Failed to load audit row: ${response.status} ${response.statusText}`,
          code: `HTTP_${response.status}`,
        };
      }

      const data: AuditListResponse = await response.json();
      return { kind: 'success', data };
    } catch (error) {
      return {
        kind: 'error',
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  async downloadEvidencePack(rowId?: string): Promise<AuditClientState> {
    try {
      const url = new URL('/api/v1/exports', globalThis.location.origin);
      
      const body = {
        kind: 'evidence-pack',
        ...(rowId && { rowId }),
      };

      const response = await this.fetchFn(url.toString(), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (response.status === 404 || response.status === 501) {
        return { kind: 'backend-not-deployed' };
      }

      if (!response.ok) {
        await response.text().catch(() => 'Unknown error'); // Error body for debugging
        return {
          kind: 'error',
          message: `Failed to generate evidence pack: ${response.status} ${response.statusText}`,
          code: `HTTP_${response.status}`,
        };
      }

      // Evidence pack generation starts async process
      const data = await response.json();
      return { kind: 'success', data };
    } catch (error) {
      return {
        kind: 'error',
        message: error instanceof Error ? error.message : 'Network error',
      };
    }
  }
}

// Default client instance
export const auditClient = new AuditDataClient();